import { ApiError } from "@/api/client";
import { REMOTE_SURVEY } from "@/constants/remoteSurvey";

export function photoUploadRequirements(): string {
  const { maxBytes, minDimensionPx } = REMOTE_SURVEY.hygiene;
  const maxMb = Math.round(maxBytes / (1024 * 1024));
  return `JPEG, PNG or HEIC · max ${maxMb} MB · at least ${minDimensionPx}×${minDimensionPx} pixels`;
}

export function formatSurveyPhotoError(err: unknown): string {
  const fallback = "Upload failed. Please try again.";
  let detail: string | undefined;

  if (typeof err === "string") {
    detail = err.trim() || undefined;
  } else if (err instanceof ApiError) {
    detail = err.errors?.file?.[0] ?? err.message;
  } else if (err instanceof Error && err.message.trim()) {
    detail = err.message.trim();
  }

  if (!detail) return fallback;
  if (/^upload failed/i.test(detail)) return detail;
  return `Upload failed: ${detail}`;
}

export async function inspectPhotoFile(file: File): Promise<string | null> {
  const { minBytes, maxBytes, minDimensionPx, allowedMimes } = REMOTE_SURVEY.hygiene;
  const mime = file.type === "image/jpg" ? "image/jpeg" : file.type;
  if (mime && !allowedMimes.includes(mime) && !mime.startsWith("image/")) {
    return "Please upload a JPEG, PNG, or HEIC photo.";
  }
  if (file.size > maxBytes) {
    return "This file is over 25 MB. Please take a photo rather than a video.";
  }

  const dimensions = await readImageSize(file);
  if (dimensions) {
    if (dimensions.width < minDimensionPx || dimensions.height < minDimensionPx) {
      return `This photo is too low resolution (${dimensions.width}×${dimensions.height}). Please retake it.`;
    }
    return null;
  }

  if (file.size < minBytes) {
    return "This photo is too small or looks empty. Please retake it.";
  }
  return null;
}

function readImageSize(file: File): Promise<{ width: number; height: number } | null> {
  if (typeof Image === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}
